
// src/components/MarketPlaceCards.jsx
import React, { useState, useMemo, useEffect } from 'react';
import { Grid, Box, Pagination, Typography, Skeleton, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ProductCard from './ProductCard';

const MarketPlaceCards = ({ rows = [], pageSize: defaultPageSize = 10, loading = false, onSelect }) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm')); // ~mobile
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md')); // small tablets

  // Adapt pageSize for mobile automatically (can still be overridden by prop)
  const pageSize = isXs ? Math.min(defaultPageSize, 5) : isSm ? Math.min(defaultPageSize, 8) : defaultPageSize;

  const [page, setPage] = useState(1); // 1-based for MUI Pagination

  useEffect(() => {
    // Reset to page 1 when rows change so users always see new results immediately
    setPage(1);
  }, [rows]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const start = (page - 1) * pageSize;
  const end = start + pageSize;

  const pagedRows = useMemo(() => rows.slice(start, end), [rows, start, end]);

  return (
    <Box sx={{ width: '100%', mt: { xs: 1, sm: 2 }, px: { xs: 1, sm: 0 } }}>
      {/* Loading skeletons */}
      {loading && (
        <Grid container spacing={isXs ? 1.5 : 2}>
          {Array.from({ length: pageSize }).map((_, i) => (
            <Grid item xs={12} key={`skeleton-${i}`}>
              <Skeleton
                variant="rounded"
                height={isXs ? 80 : 120}
                sx={{
                  borderRadius: 2,
                }}
                aria-label="Loading product"
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Empty state */}
      {!loading && rows.length === 0 && (
        <Box
          role="status"
          aria-live="polite"
          sx={{
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            p: { xs: 2, sm: 3 },
            textAlign: 'center',
            color: 'text.secondary',
            backgroundColor: (t) => (t.palette.mode === 'dark' ? 'background.default' : '#fafafa'),
          }}
        >
          <Typography variant={isXs ? 'subtitle2' : 'subtitle1'} fontWeight={700}>
            No products found
          </Typography>
          <Typography variant={isXs ? 'caption' : 'body2'}>
            Try refining your search criteria.
          </Typography>
        </Box>
      )}

      {/* Cards */}
      {!loading && rows.length > 0 && (
        <>
          <Grid container spacing={isXs ? 1.5 : 2}>
            {pagedRows.map((product) => (
              <Grid item xs={12} key={product.id_prod}>
                <ProductCard product={product} onSelect={onSelect} />
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          <Box
            display="flex"
            justifyContent="center"
            mt={isXs ? 1.5 : 2}
            sx={{
              position: 'sticky',
              bottom: { xs: 8, sm: 0 }, // keep pagination accessible on mobile
              backgroundColor: 'transparent',
              zIndex: 1,
            }}
          >
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              siblingCount={isXs ? 0 : 1}
              boundaryCount={isXs ? 1 : 2}
              size={isXs ? 'small' : 'medium'}
              showFirstButton={!isXs}
              showLastButton={!isXs}
              aria-label="Product pagination"
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default MarketPlaceCards;
